export const beforeItems = [
  'You struggle to determine ideal tooth proportions for different patients.',
  'You rely heavily on software-generated designs without a consistent clinical evaluation process.',
  'You feel uncertain when analyzing smile lines, gingival symmetry, and facial harmony.',
  'You spend too much time adjusting digital simulations.',
  'You find it difficult to translate an aesthetic concept into a practical restorative plan.',
  'You struggle to communicate your design decisions clearly to patients and dental technicians.',
];

export const afterItems = [
  'Follow a structured sequence for facial and dental aesthetic analysis.',
  'Evaluate tooth proportions using established aesthetic principles.',
  'Understand how smile lines, gingival contours, and facial features influence design decisions.',
  'Use practical planning checklists before presenting a proposed treatment.',
  'Organize your digital smile design workflow more efficiently.',
  'Communicate aesthetic objectives with greater clarity.',
];

export const painPoints = [
  {
    title: 'You Struggle With Tooth Proportions',
    body: 'You know the final smile should look harmonious, but you’re not always sure how to evaluate tooth width, length, symmetry, and individual facial characteristics.',
  },
  {
    title: 'Digital Smile Design Takes Too Much Time',
    body: 'You spend hours reviewing photographs, adjusting proportions, and refining simulations without a clear sequence to follow.',
  },
  {
    title: 'You’re Unsure Where to Start',
    body: 'Should you begin with facial analysis, the smile line, gingival architecture, or tooth dimensions? Without a structured workflow, the planning process can become unnecessarily complicated.',
  },
  {
    title: 'Your Digital Design Doesn’t Always Translate Into the Expected Clinical Result',
    body: 'You want to better understand the restorative, periodontal, orthodontic, and functional considerations that influence whether an aesthetic proposal is clinically appropriate.',
  },
  {
    title: 'You Depend Too Much on Digital Software',
    body: 'Software can generate attractive simulations, but it cannot replace patient-specific clinical assessment. You want to understand the principles behind the design.',
  },
  {
    title: 'You Want Patients to Better Understand Your Treatment Vision',
    body: 'You know what you’re trying to achieve, but communicating the proposed aesthetic changes isn’t always easy.',
  },
];

export const volumes = [
  {
    number: 'I',
    title: 'Facial Aesthetics Blueprint™',
    subtitle: 'The Foundation of Every Harmonious Smile',
    intro: 'Understand the facial characteristics that influence aesthetic dental planning.',
    topics: [
      'Facial symmetry and proportional relationships',
      'Facial midline versus dental midline',
      'Lip dynamics and smile analysis',
      'Smile arc evaluation',
      'Incisal display considerations',
      'Standardized photographic assessment',
      'Common facial analysis mistakes',
    ],
    takeaway: 'A visual framework for evaluating facial characteristics before developing an aesthetic proposal.',
  },
  {
    number: 'II',
    title: 'Dental Proportion Mastery™',
    subtitle: 'The Science Behind Natural-Looking Tooth Design',
    intro: 'Learn how to evaluate dental proportions without treating every smile as if it should look identical.',
    topics: [
      'Tooth width-to-length relationships',
      'Central incisor dominance',
      'Relative tooth proportions',
      'Dental symmetry and asymmetry',
      'Incisal edge positioning',
      'Tooth shape and morphology',
      'Limitations of universal aesthetic ratios',
    ],
    takeaway: 'A structured reference for evaluating tooth dimensions and creating individualized aesthetic objectives.',
  },
  {
    number: 'III',
    title: 'Gingival Aesthetics Roadmap™',
    subtitle: 'The Missing Piece of Smile Harmony',
    intro: 'Understand how gingival architecture influences the appearance of a smile.',
    topics: [
      'Gingival zenith positioning',
      'Gingival margin relationships',
      'Smile line classification',
      'Papilla considerations',
      'Gingival asymmetries',
      'Aesthetic crown length considerations',
      'When periodontal evaluation may be necessary',
    ],
    takeaway: 'A visual approach to identifying gingival factors that should be considered during interdisciplinary planning.',
  },
  {
    number: 'IV',
    title: 'Digital Smile Design Workflow™',
    subtitle: 'From Clinical Photography to Visual Treatment Planning',
    intro: 'Organize the essential stages of digital smile design into a practical sequence.',
    topics: [
      'Preparing photographic records',
      'Selecting appropriate reference images',
      'Establishing facial and dental reference lines',
      'Evaluating smile proportions digitally',
      'Developing preliminary aesthetic simulations',
      'Reviewing proposed changes',
      'Identifying the limitations of two-dimensional simulations',
    ],
    takeaway: 'A repeatable digital planning workflow that can be adapted to different software platforms.',
  },
  {
    number: 'V',
    title: 'Restorative Smile Planning Blueprint™',
    subtitle: 'Turning Aesthetic Concepts Into Clinically Realistic Treatment Plans',
    intro: 'Learn to evaluate whether a proposed smile design aligns with the patient’s restorative and functional needs.',
    topics: [
      'Restorative treatment considerations',
      'Veneer planning principles',
      'Crown and restorative alternatives',
      'Tooth preservation',
      'Occlusal and functional considerations',
      'Interdisciplinary treatment planning',
      'Identifying when specialist evaluation is appropriate',
    ],
    takeaway: 'A framework for connecting aesthetic goals with realistic, patient-specific treatment considerations.',
  },
  {
    number: 'VI',
    title: 'Smile Design Validation Roadmap™',
    subtitle: 'The Final Review Before Presenting Your Aesthetic Plan',
    intro: 'Develop a consistent approach to reviewing aesthetic proposals before discussing treatment options with patients.',
    topics: [
      'Final facial harmony assessment',
      'Dental proportion verification',
      'Gingival relationship review',
      'Functional considerations',
      'Digital simulation limitations',
      'Patient expectation alignment',
      'Treatment presentation and communication',
    ],
    takeaway: 'A structured review process for evaluating whether the proposed design is coherent, understandable, and clinically appropriate.',
  },
];

export const learningTools = [
  { title: 'Visual Proportion Reference Charts', body: 'Illustrated references for evaluating dental proportions, tooth morphology, and aesthetic relationships.' },
  { title: 'Smile Planning Decision Maps', body: 'Visual pathways that help organize the assessment of facial, dental, gingival, and restorative considerations.' },
  { title: 'Clinical Evaluation Checklists', body: 'Structured lists for reviewing important information during different planning stages.' },
  { title: 'Photographic Assessment Guides', body: 'Visual references for organizing photographic records and evaluating smile characteristics.' },
  { title: 'Illustrated Planning Scenarios', body: 'Educational examples that demonstrate how different aesthetic variables can influence treatment planning.' },
];

export const bonuses = [
  {
    number: 1,
    title: 'The Smile Analysis Checklist™',
    subtitle: 'Your Pre-Design Clinical Review Companion',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/gLsf4zNZ87CrfzUvqMW7en/BONUS_1_SIMLE-EN.png',
    description: 'A structured checklist covering important facial, dental, gingival, and functional factors to consider before developing an aesthetic smile proposal.',
    includes: ['Facial analysis review', 'Dental proportion evaluation', 'Gingival assessment', 'Photographic record verification', 'Functional considerations', 'Final planning review'],
    why: 'Helps you organize your initial evaluation and recognize information that may require further clinical assessment.',
  },
  {
    number: 2,
    title: 'The Dental Proportions Visual Atlas™',
    subtitle: 'A Quick-Reference Guide to Smile Aesthetics',
    value: 29,
    image: 'https://cdn.magicpatterns.com/uploads/7bP3TLQQUrsJAAwVCiKfHt/BONUS_2_SMILE-EN.png',
    description: 'An illustrated collection of aesthetic dental relationships to help you understand the visual principles behind natural-looking smiles.',
    includes: ['Tooth morphology comparisons', 'Width-to-length references', 'Incisal edge relationships', 'Symmetry and asymmetry examples', 'Smile arc illustrations', 'Common aesthetic proportion misconceptions'],
    why: 'Makes dental aesthetic concepts easier to visualize and compare during planning.',
  },
  {
    number: 3,
    title: 'The Digital Smile Photography Guide™',
    subtitle: 'Capture Better Records for More Informed Planning',
    value: 27,
    image: 'https://cdn.magicpatterns.com/uploads/fecauntQ3yTPhxqa1UePS9/BONUS_3_SMILE-EN.png',
    description: 'A practical guide to organizing standardized photographic records for smile design assessment.',
    includes: ['Recommended photographic views', 'Patient positioning considerations', 'Lighting and framing principles', 'Common photography errors', 'Image consistency checklists', 'Record organization guidelines'],
    why: 'Helps improve the consistency of the photographic information used during aesthetic evaluation.',
  },
  {
    number: 4,
    title: 'The Smile Design Planning Workbook™',
    subtitle: 'Organize Every Stage of Your Aesthetic Evaluation',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/8xp67tfZZsagJLyrXeJpFW/BONUS_4_SMILE-EN.png',
    description: 'A printable and digital workbook for documenting the key considerations involved in smile design planning.',
    includes: ['Facial analysis worksheets', 'Dental proportion planning pages', 'Gingival assessment forms', 'Aesthetic objective templates', 'Treatment planning review sheets', 'Final design evaluation pages'],
    why: 'Gives you a consistent way to organize your planning notes and clinical observations.',
  },
  {
    number: 5,
    title: 'The Patient Smile Consultation Playbook™',
    subtitle: 'Explain Your Aesthetic Vision With Greater Clarity',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/ahKxDhz83mm1AajBQhWM5M/BONUS_5_SMILE-EN.png',
    description: 'A practical communication guide to help you explain aesthetic treatment planning concepts to patients.',
    includes: ['Smile design consultation frameworks', 'Ways to explain aesthetic proportions', 'Digital simulation expectation-setting', 'Communication about treatment alternatives', 'Common patient questions', 'Shared decision-making prompts'],
    why: 'Helps patients understand the difference between an aesthetic simulation, a proposed treatment plan, and a clinically achievable outcome.',
  },
];

export const testimonials = [
  {
    topic: 'Aesthetic Planning',
    quote: 'What I appreciate most is the clear progression from facial analysis to dental proportions. Having the concepts organized into separate volumes makes the planning process much easier to study and review.',
    role: 'Cosmetic Dentist',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/98dec45a-a47a-4b6a-9692-f0123ea47ba1.jpg',
  },
  {
    topic: 'Digital Workflow',
    quote: 'The digital smile design workflow is especially helpful because it explains the reasoning behind the process instead of focusing only on software tools.',
    role: 'General Dentist',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/9341b4e9-8c7b-4d9e-9808-b140eb112b84.jpg',
  },
  {
    topic: 'Dental Proportions',
    quote: 'The visual references make it easier to understand how tooth proportions, incisal edges, and facial characteristics interact in aesthetic planning.',
    role: 'Restorative Dentist',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/7df1ec79-e923-416a-9b6d-d28bfd215973.jpg',
  },
  {
    topic: 'Clinical Organization',
    quote: 'The checklists and worksheets are the kind of resources I would want available when reviewing an aesthetic treatment proposal.',
    role: 'Dental Professional',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/844ed7d5-b4b5-4b2e-847a-2cfc8917c809.jpg',
  },
  {
    topic: 'Patient Communication',
    quote: 'I particularly like the emphasis on communicating realistic expectations. Digital smile design is much more useful when patients understand what the simulation can and cannot promise.',
    role: 'Cosmetic Dentistry Professional',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/bc4b2d7b-b2ce-4e43-a4dc-27a2ce67fd7e.jpg',
  },
  {
    topic: 'Final Validation',
    quote: 'The validation volume gives me a clear final review before I sit down with the patient. My case presentations feel more consistent and much easier to explain.',
    role: 'Prosthodontist',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/b2eeead6-391d-4ff4-afab-210011cc8d6e.jpg',
  },
];

export const faqs = [
  {
    q: 'How will I receive SMILE DESIGN ROADMAP™?',
    a: 'The product is designed for digital delivery. Right after completing your purchase, you’ll receive access to the six-volume library and all five bonuses by email.',
  },
  {
    q: 'Are the materials downloadable?',
    a: 'Yes. The product is designed as a collection of digital PDF educational resources that you can download and consult on compatible devices.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. The complete package is offered for a single payment of $27 USD. There are no recurring subscription fees.',
  },
  {
    q: 'Is SMILE DESIGN ROADMAP™ suitable for dentists in the United States?',
    a: 'The materials cover general principles of aesthetic dental planning relevant to professional education. They are not a substitute for applicable U.S. licensing requirements, professional training, clinical standards, or patient-specific assessment.',
  },
  {
    q: 'Is this only for cosmetic dentists?',
    a: 'No. The educational content may also be useful for general dentists, restorative dentists, prosthodontists, and other appropriately trained dental professionals interested in aesthetic treatment planning.',
  },
  {
    q: 'Do I need expensive digital smile design software?',
    a: 'No specific software subscription is required to study the educational material. Some concepts involve digital workflows, but the roadmap focuses on planning principles that can be adapted to different clinical environments.',
  },
  {
    q: 'Does the roadmap teach me how to perform veneer or crown procedures?',
    a: 'The roadmap introduces relevant restorative planning considerations. It is not a hands-on procedural certification program and does not replace supervised clinical training.',
  },
  {
    q: 'Will this guarantee better treatment outcomes?',
    a: 'No educational resource can guarantee a specific clinical outcome. SMILE DESIGN ROADMAP™ is designed to support structured learning and informed planning. Actual treatment decisions depend on professional judgment, patient-specific findings, and appropriate clinical standards.',
  },
  {
    q: 'Will I receive lifetime access?',
    a: 'Yes. The offer includes lifetime access to the purchased digital materials, subject to the seller’s published access terms.',
  },
  {
    q: 'What if I’m not satisfied with my purchase?',
    a: 'You can request a refund within seven days of purchase according to our published refund policy.',
  },
];

export const recentBuyers = [
  { name: 'María G.', country: 'Mexico', flag: '🇲🇽' },
  { name: 'Carlos R.', country: 'Colombia', flag: '🇨🇴' },
  { name: 'Valentina P.', country: 'Argentina', flag: '🇦🇷' },
  { name: 'Javier M.', country: 'Spain', flag: '🇪🇸' },
  { name: 'Camila S.', country: 'Chile', flag: '🇨🇱' },
  { name: 'Andrés L.', country: 'Peru', flag: '🇵🇪' },
  { name: 'Daniela F.', country: 'Ecuador', flag: '🇪🇨' },
  { name: 'Luis H.', country: 'Guatemala', flag: '🇬🇹' },
  { name: 'Sofía V.', country: 'Uruguay', flag: '🇺🇾' },
  { name: 'Diego A.', country: 'Costa Rica', flag: '🇨🇷' },
  { name: 'Gabriela T.', country: 'Dominican Republic', flag: '🇩🇴' },
  { name: 'Fernando C.', country: 'Paraguay', flag: '🇵🇾' },
  { name: 'Lucía B.', country: 'Bolivia', flag: '🇧🇴' },
  { name: 'Ricardo N.', country: 'Panama', flag: '🇵🇦' },
  { name: 'Isabel O.', country: 'Puerto Rico', flag: '🇵🇷' },
];
